import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, AlertCircle, Loader, ArrowLeft } from 'lucide-react';
import './ResetPassword.css';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [isValidToken, setIsValidToken] = useState(null);

    useEffect(() => {
        // Verify token when component mounts
        if (token) {
            verifyResetToken();
        } else {
            setIsValidToken(false);
            setMessage('No reset token provided');
        }
    }, [token]);

    // ✅ Fixed API URL logic
    const getApiUrl = () => {
        // For development
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            return 'http://localhost:3001/api';
        }
        
        // For production - your .env has REACT_APP_API_URL=/api
        // This means it will use the same domain with /api prefix
        return process.env.REACT_APP_API_URL || '/api';
    };

    const verifyResetToken = async () => {
        setIsLoading(true);
        try {
            console.log('🔍 Verifying token:', token?.substring(0, 8) + '...');
            console.log('🔍 Using API URL:', getApiUrl());
            
            const response = await fetch(`${getApiUrl()}/verify-reset-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token })
            });

            console.log('🔍 Verification response status:', response.status);
            const data = await response.json();
            console.log('🔍 Verification response data:', data);
            
            setIsValidToken(data.success);
            
            if (!data.success) {
                setMessage(data.message || 'Invalid or expired reset link');
            } else {
                console.log('✅ Token verified successfully');
            }
        } catch (error) {
            console.error('❌ Token verification error:', error);
            setIsValidToken(false);
            setMessage('Error verifying reset link. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setMessage(''); // Clear message when user types
    };

    const validatePassword = (password) => {
        const minLength = password.length >= 8;
        const hasUpper = /[A-Z]/.test(password);
        const hasLower = /[a-z]/.test(password);
        const hasNumber = /\d/.test(password);

        return {
            minLength,
            hasUpper,
            hasLower,
            hasNumber,
            isValid: minLength && hasUpper && hasLower && hasNumber
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.password !== formData.confirmPassword) {
            setMessage('Passwords do not match');
            return;
        }

        const passwordValidation = validatePassword(formData.password);
        if (!passwordValidation.isValid) {
            setMessage('Password does not meet requirements');
            return;
        }

        setIsLoading(true);
        setMessage('');

        try {
            console.log('🔐 Submitting password reset...');
            console.log('🔐 Using API URL:', getApiUrl());
            
            const response = await fetch(`${getApiUrl()}/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token,
                    newPassword: formData.password
                })
            });

            console.log('🔐 Reset response status:', response.status);
            const data = await response.json();
            console.log('🔐 Reset response data:', data);

            if (data.success) {
                setMessage('Password reset successful! Redirecting to login...');
                setTimeout(() => {
                    navigate('/login', { 
                        state: { 
                            message: 'Password reset successful! Please log in with your new password.' 
                        }
                    });
                }, 2000);
            } else {
                setMessage(data.message || 'Failed to reset password');
            }
        } catch (error) {
            console.error('❌ Reset password error:', error);
            setMessage('Network error. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const passwordValidation = validatePassword(formData.password);
    const isFormValid = passwordValidation.isValid && formData.password === formData.confirmPassword;

    // Loading state for initial token verification
    if (isValidToken === null) {
        return (
            <div className="reset-password-container">
                <div className="reset-password-card">
                    <div className="reset-loading">
                        <Loader className="loading-spinner" size={48} />
                        <p>Verifying reset link...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Invalid token state
    if (isValidToken === false) {
        return (
            <div className="reset-password-container">
                <div className="reset-password-card">
                    <div className="reset-error">
                        <AlertCircle className="error-icon" size={64} />
                        <h2>Invalid Reset Link</h2>
                        <p>{message || 'The reset link is invalid or has expired. Please request a new password reset.'}</p>
                        <div className="reset-error-actions">
                            <button 
                                onClick={() => navigate('/login')}
                                className="back-to-login"
                            >
                                <ArrowLeft size={18} />
                                Back to Login
                            </button>
                            <button 
                                onClick={() => navigate('/forgot-password')}
                                className="request-new-reset"
                            >
                                Request New Reset
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="reset-password-container">
            <div className="reset-password-card">
                <div className="reset-password-header">
                    <div className="reset-header-icon">
                        <Lock size={32} />
                    </div>
                    <h1>Set New Password</h1>
                    <p>Enter your new password below</p>
                </div>

                <form onSubmit={handleSubmit} className="reset-password-form">
                    {/* New Password Input */}
                    <div className="input-group">
                        <label className="input-label">New Password</label>
                        <div className="input-wrapper">
                            <Lock className="input-icon" size={20} />
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                className="reset-input"
                                placeholder="Enter your new password"
                                required
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                className="toggle-password"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    {/* Confirm Password Input */}
                    <div className="input-group">
                        <label className="input-label">Confirm Password</label>
                        <div className="input-wrapper">
                            <Lock className="input-icon" size={20} />
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                className="reset-input"
                                placeholder="Confirm your new password"
                                required
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                className="toggle-password"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            >
                                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    {/* Password Requirements */}
                    {formData.password && (
                        <div className="password-requirements">
                            <h4>Password Requirements</h4>
                            <div className="requirements-list">
                                <div className={`requirement-item ${passwordValidation.minLength ? 'met' : ''}`}>
                                    {passwordValidation.minLength ? 
                                        <CheckCircle className="requirement-icon" size={16} /> : 
                                        <XCircle className="requirement-icon" size={16} />
                                    }
                                    <span>At least 8 characters</span>
                                </div>
                                <div className={`requirement-item ${passwordValidation.hasUpper ? 'met' : ''}`}>
                                    {passwordValidation.hasUpper ? 
                                        <CheckCircle className="requirement-icon" size={16} /> : 
                                        <XCircle className="requirement-icon" size={16} />
                                    }
                                    <span>One uppercase letter</span>
                                </div>
                                <div className={`requirement-item ${passwordValidation.hasLower ? 'met' : ''}`}>
                                    {passwordValidation.hasLower ? 
                                        <CheckCircle className="requirement-icon" size={16} /> : 
                                        <XCircle className="requirement-icon" size={16} />
                                    }
                                    <span>One lowercase letter</span>
                                </div>
                                <div className={`requirement-item ${passwordValidation.hasNumber ? 'met' : ''}`}>
                                    {passwordValidation.hasNumber ? 
                                        <CheckCircle className="requirement-icon" size={16} /> : 
                                        <XCircle className="requirement-icon" size={16} />
                                    }
                                    <span>One number</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Password Match Indicator */}
                    {formData.confirmPassword && (
                        <div className="password-match-indicator">
                            <div className={`requirement-item ${formData.password === formData.confirmPassword ? 'met' : ''}`}>
                                {formData.password === formData.confirmPassword ? 
                                    <CheckCircle className="requirement-icon" size={16} /> : 
                                    <XCircle className="requirement-icon" size={16} />
                                }
                                <span>Passwords match</span>
                            </div>
                        </div>
                    )}

                    {/* Message Display */}
                    {message && (
                        <div className={`reset-message ${message.includes('successful') ? 'success' : 'error'}`}>
                            {message}
                        </div>
                    )}

                    {/* Submit Button */}
                    <button 
                        type="submit" 
                        className={`reset-submit-btn ${!isFormValid ? 'disabled' : ''}`}
                        disabled={isLoading || !isFormValid}
                    >
                        {isLoading ? (
                            <>
                                <Loader className="loading-spinner" size={20} />
                                Updating Password...
                            </>
                        ) : (
                            <>
                                <Lock size={20} />
                                Update Password
                            </>
                        )}
                    </button>
                </form>

                <div className="reset-footer">
                    <button 
                        type="button"
                        onClick={() => navigate('/login')}
                        className="back-to-login-btn"
                    >
                        <ArrowLeft size={18} />
                        Back to Login
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
